<script setup>
import { computed, inject } from 'vue';
import { shopMyHubsUrl } from '../../helpers/helper';
import { CAPABILITIES, can } from '../../helpers/roles';
import { useAuthStore } from '../../stores/auth';
import IconBack from '../icons/IconBack.vue';
import HubBrand from './HubBrand.vue';
import HubNav from './HubNav.vue';

defineProps({
  collapsed: { type: Boolean, default: false },
  // In drawer mode the collapse toggle is meaningless — the viewport, not the
  // viewer, decides the width — so it is replaced by a close control.
  drawer: { type: Boolean, default: false },
});

defineEmits(['toggle', 'close', 'navigate']);

const authStore = useAuthStore();
const envURLs = inject('envURLs', {});

// The way back up to organisation level. Gated the same way Policy lines,
// Registration and Settings already are — a broker or a policyholder has no
// organisation view to go to, only the hub they are standing in.
const canLeaveHub = computed(() =>
  can(authStore.userRole, CAPABILITIES.MANAGE_HUB_CONTENT, authStore.capabilities)
);

// A new tab, deliberately: this leaves the hub for a different application
// (the eco app), and losing the page you were on to get there would be a
// worse trade than one extra tab.
const goToMyHubs = () => {
  window.open(shopMyHubsUrl(envURLs.eco), '_blank', 'noopener');
};
</script>

<template>
  <aside class="hub-sidebar" :class="{ 'hub-sidebar--collapsed': collapsed }">
    <button
      v-if="canLeaveHub"
      type="button"
      class="hub-sidebar__my-hubs"
      :class="{ 'hub-sidebar__my-hubs--collapsed': collapsed }"
      :title="collapsed ? 'My Hubs' : null"
      @click="goToMyHubs"
    >
      <IconBack class="hub-sidebar__my-hubs-icon" />
      <span v-if="!collapsed" class="hub-sidebar__my-hubs-label">My Hubs</span>
    </button>

    <div class="hub-sidebar__head">
      <HubBrand :collapsed="collapsed" />

      <button
        v-if="drawer"
        type="button"
        class="hub-sidebar__toggle"
        aria-label="Close navigation"
        @click="$emit('close')"
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
        </svg>
      </button>
      <button
        v-else
        type="button"
        class="hub-sidebar__toggle"
        :aria-label="collapsed ? 'Expand navigation' : 'Collapse navigation'"
        :aria-expanded="collapsed ? 'false' : 'true'"
        @click="$emit('toggle')"
      >
        <svg
          class="hub-sidebar__chevron"
          width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"
        >
          <path d="M10 3.5L5.5 8l4.5 4.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>

    <HubNav :collapsed="collapsed" @navigate="$emit('navigate')" />
  </aside>
</template>

<style scoped>
.hub-sidebar {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  height: 100%;
  padding: 12px;
  background: #fff;
  border-radius: 0 12px 12px 0;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3), 0 2px 6px 2px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.hub-sidebar__my-hubs {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  min-height: 34px;
  padding: 0 13px;
  margin-bottom: 4px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: var(--xl-color-on-surface-variant);
  font-family: var(--xl-font-family);
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
}

.hub-sidebar__my-hubs:hover {
  background: var(--xlm-theme-state-layers-light-on-surface-opacity-008, rgba(30, 26, 31, 0.08));
}

.hub-sidebar__my-hubs:focus-visible {
  outline: 2px solid #5e2f78;
  outline-offset: 2px;
}

.hub-sidebar__my-hubs-icon {
  flex-shrink: 0;
  width: 15px;
  height: 15px;
}

.hub-sidebar__my-hubs--collapsed {
  justify-content: center;
  padding: 8px 0;
}

.hub-sidebar__head {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
}

.hub-sidebar--collapsed {
  padding: 12px 6px;
  align-items: center;
}

.hub-sidebar--collapsed .hub-sidebar__head {
  flex-direction: column;
  gap: 0;
}

.hub-sidebar__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  margin-left: auto;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--XL-M-Theme-sys-light-on-surface-variant, #4b444d);
  line-height: 0;
  cursor: pointer;
}

.hub-sidebar--collapsed .hub-sidebar__toggle {
  margin-left: 0;
}
/* Drawn rather than typed: a chevron glyph's ink sits 2.2px below the centre
   of its line box, so it never looks centred in a round button, and it needs
   the webfont to have loaded to look right at all. */
.hub-sidebar__chevron {
  transition: transform 0.16s ease;
}
.hub-sidebar--collapsed .hub-sidebar__chevron {
  transform: rotate(180deg);
}
@media (prefers-reduced-motion: reduce) {
  .hub-sidebar__chevron { transition: none; }
}

.hub-sidebar__toggle:hover {
  background: var(--xlm-theme-state-layers-light-on-surface-opacity-008, rgba(30, 26, 31, 0.08));
}

.hub-sidebar__toggle:focus-visible {
  outline: 2px solid #5e2f78;
  outline-offset: 2px;
}
</style>
